import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import { useTranslation } from "next-i18next";
import { Card, Button } from "@filadd/phi-plus-core";
import { css } from "@filadd/phi-plus-styled-system/css";
import { CaretRight, Spinner } from "@phosphor-icons/react";

import { studyProgramPath } from "helpers/redirects";
import { LinearProgressWithLabel } from "components/commons";

import type { ExpandedStudyProgramResponse } from "types/courses/study-program";

interface Props
  extends Pick<
    ExpandedStudyProgramResponse,
    "short_slug" | "thumbnail_url" | "title" | "stats" | "progress"
  > {
  courseSlug: string;
}

export const StudyProgramCard = ({
  courseSlug,
  short_slug,
  thumbnail_url,
  title,
  stats,
  progress,
}: Props) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const endIcon = isLoading ? (
    <Spinner
      className={css({
        animation: "spin 1s linear infinite",
      })}
    />
  ) : (
    <CaretRight />
  );

  return (
    <Card.Root
      className={css({
        display: "flex",
        flexDir: "row",
        height: "100%",
        borderRadius: "xl",
        p: 0,
        boxShadow: "sm",
      })}
    >
      {!!thumbnail_url && (
        <Image
          src={thumbnail_url}
          alt={title}
          width={150}
          height={150}
          className={css({
            objectFit: "cover",
            height: "full",
            width: "full",
            maxH: 20,
            maxW: 20,
            mt: 4,
            ml: 4,
            borderRadius: "lg",
            lg: {
              maxW: "100px",
              maxH: "full",
              mt: 0,
              ml: 0,
              borderRadius: "none",
            },
            xl: {
              maxW: "150px",
            },
          })}
        />
      )}

      <div
        className={css({
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          p: 4,
        })}
      >
        <div>
          <LinearProgressWithLabel
            percentage={progress ? Math.ceil(progress.percentage_completed) : 0}
          />

          <h3
            className={css({
              textStyle: "titleMedium",
            })}
          >
            {title}
          </h3>

          {stats && (
            <p
              className={css({
                color: "neutral.700",
                mt: 2,
              })}
            >
              {`${stats.units_amount} ${t("common:unit", {
                count: stats.units_amount,
              })}`}
            </p>
          )}
        </div>

        <div
          className={css({
            mt: 4,
            display: "flex",
            flexDir: "row",
            gap: 2,
            m: "0 0 auto auto",
          })}
        >
          <Link href={studyProgramPath(courseSlug, short_slug)}>
            <Button
              onClick={() => {
                setIsLoading(true);
              }}
              disabled={isLoading}
            >
              {t("common:open")}
              {endIcon}
            </Button>
          </Link>
        </div>
      </div>
    </Card.Root>
  );
};
